import { Box, Flex } from "@chakra-ui/react";
import { useMemo, useState, useEffect } from "react";
import { DEFAULT_SPACE_COLOR } from "../../../constants/space";

interface CheckBoxProps {
  // Basic
  isSelected: boolean;
  baseColor: string;
  onClick?: (e: React.MouseEvent) => void;

  // Select
  size?: number;
  enableAnimation?: boolean;
  checkColor?: string;
  borderRadius?: string;
}

// NOTE Calendar Visibility 조정 checkbox 공통 UI 컴포넌트
// Main Calendar Dropdown/Settings내 Calendar Visibility 조정에 활용
// animation 여부는 조이에게 확인 중

const CalendarVisibilityCheckBox = ({
  isSelected,
  baseColor,
  onClick,
  size = 14,
  enableAnimation = true,
  checkColor = "#1a1c1c",
  borderRadius = "3px",
}: CheckBoxProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPointerFine, setIsPointerFine] = useState(false);
  const [isAnimationActive, setIsAnimationActive] = useState(false);

  // 배경색 계산
  const backgroundColor = useMemo(() => {
    return isSelected || (isHovered && isPointerFine) ? baseColor : `transparent`;
  }, [baseColor, isSelected, isHovered, isPointerFine]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(pointer: fine)");
    setIsPointerFine(mediaQuery.matches);

    const handleMediaChange = (e: MediaQueryListEvent) => {
      setIsPointerFine(e.matches);
    };

    mediaQuery.addEventListener("change", handleMediaChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange);
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    // 애니메이션 활성화 여부 결정
    if (enableAnimation && !isSelected) {
      setIsAnimationActive(true);
    } else {
      setIsAnimationActive(false);
    }

    if (onClick) onClick(e);
  };

  return (
    <Flex
      py={0.5}
      flexShrink={0}
      id="checkbox-wrapper"
    >
      <Box
        className={`checkbox ${
          isSelected ? "checkboxCompleted" : ""
        } ${isHovered && isPointerFine && !isSelected ? "checkboxHovered" : ""} ${
          isAnimationActive ? "animationActive" : ""
        }`}
        minW={`${size}px`}
        minH={`${size}px`}
        borderRadius={borderRadius}
        boxShadow={`0 0 0 1px ${isSelected || isHovered ? baseColor : DEFAULT_SPACE_COLOR} inset`}
        position="relative"
        backgroundColor={backgroundColor}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        sx={{
          // 체크박스 공통 스타일 - 체크 표시에 대한 기본 스타일
          "&.checkboxCompleted::before": {
            boxSizing: "border-box",
            content: '""',
            position: "absolute",
            top: "35%",
            left: "15%",
            borderRight: `2px solid ${checkColor}`,
            borderBottom: `2px solid ${checkColor}`,
            transformOrigin: "0% 100%",
            width: `${size * 0.36}px`, // 크기에 비례하게 조정
            height: `${size * 0.64}px`, // 크기에 비례하게 조정
            transform: "translate3d(0, -0.5em, 0) rotate(45deg)",
            display: "block",
          },

          // hover와 pointer: fine인 경우에만 호버 체크마크 표시
          "&:hover:not(.checkboxCompleted)::before": {
            boxSizing: "border-box",
            content: '""',
            position: "absolute",
            top: "35%",
            left: "15%",
            borderRight: `2px solid ${checkColor}`,
            borderBottom: `2px solid ${checkColor}`,
            transformOrigin: "0% 100%",
            width: `${size * 0.36}px`, // 크기에 비례하게 조정
            height: `${size * 0.64}px`, // 크기에 비례하게 조정
            transform: "translate3d(0, -0.5em, 0) rotate(45deg)",
            // 모바일(pointer: coarse)에서는 호버 체크 아이콘 숨기기
            "@media (pointer: coarse)": {
              display: "none !important",
            },
          },

          // pointer: coarse일 때 hover 오버라이드
          "@media (pointer: coarse)": {
            "&:hover:not(.checkboxCompleted)::before": {
              display: "none !important",
            },
          },

          // completed & animation 상태일 때 박스 애니메이션
          "&.checkboxCompleted.animationActive": {
            animation: "shrink-bounce 200ms cubic-bezier(0.4, 0, 0.23, 1) forwards",
          },

          // completed & animation 상태일 때 체크마크 애니메이션
          "&.checkboxCompleted.animationActive::before": {
            boxSizing: "border-box",
            content: '""',
            position: "absolute",
            top: "35%",
            left: "15%",
            borderRight: `2px solid transparent`,
            borderBottom: `2px solid transparent`,
            transform: "rotate(45deg)",
            transformOrigin: "0% 100%",
            display: "block !important", // 모든 환경에서 애니메이션은 표시
            animation: "checkbox-check 125ms 250ms cubic-bezier(0.4, 0, 0.23, 1) forwards",
          },
          "@keyframes shrink-bounce": {
            "0%": { transform: "scale(1)" },
            "33%": { transform: "scale(0.85)" },
            "100%": { transform: "scale(1)" },
          },
          "@keyframes checkbox-check": {
            "0%": {
              width: 0,
              height: 0,
              borderColor: checkColor,
              transform: "translate3d(0, 0, 0) rotate(45deg)",
            },
            "33%": {
              width: `${size * 0.36}px`, // 크기에 비례하게 조정
              height: 0,
              transform: "translate3d(0, 0, 0) rotate(45deg)",
            },
            "100%": {
              width: `${size * 0.36}px`, // 크기에 비례하게 조정
              height: `${size * 0.64}px`, // 크기에 비례하게 조정
              borderColor: checkColor,
              transform: "translate3d(0, -0.5em, 0) rotate(45deg)",
            },
          },
        }}
      />
    </Flex>
  );
};
export default CalendarVisibilityCheckBox;
